import { useState, useEffect, useRef } from 'react';
import { X, CheckCircle2, BookOpen, AlertCircle, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

export interface EducationMatchRule {
  field: string;
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'includes';
  value: any;
}

export interface EducationSection {
  id: string;
  title: string;
  content: string;
  bullets?: string[];
  goodExample?: string;
  riskyExample?: string;
  exception?: string;
  tags?: string[];
  priority?: number;
  match?: EducationMatchRule[];     // Legacy: treated as matchAll
  matchAll?: EducationMatchRule[];  // All must pass
  matchAny?: EducationMatchRule[];  // At least one must pass
}

export interface EducationTopic {
  id: string;
  title: string;
  sections: EducationSection[];
}

interface EducationDrawerProps {
  open: boolean;
  onClose: () => void;
  topicId: string;
  topic?: EducationTopic;
  onDismissTopic?: (topicId: string) => void;
  context?: Record<string, any>;
  onDisableAutoJump?: (topicId: string) => void;
  isAutoJumpDisabled?: boolean;
}

// Helper to evaluate a single rule
const evaluateRule = (rule: EducationMatchRule, context: any): boolean => {
  if (!context) return false;
  
  // Safely access nested properties (e.g., "decision.severity")
  const actualValue = rule.field.split('.').reduce((obj, key) => obj?.[key], context);
  
  switch (rule.op) {
    case 'eq': return actualValue === rule.value;
    case 'neq': return actualValue !== rule.value;
    case 'gt': return Number(actualValue) > Number(rule.value);
    case 'gte': return Number(actualValue) >= Number(rule.value);
    case 'lt': return Number(actualValue) < Number(rule.value);
    case 'lte': return Number(actualValue) <= Number(rule.value);
    case 'includes': 
      return Array.isArray(actualValue) 
        ? actualValue.includes(rule.value) 
        : String(actualValue || '').includes(String(rule.value));
    default: return false;
  }
};

export function EducationDrawer({
  open,
  onClose,
  topicId,
  topic,
  onDismissTopic,
  context,
  onDisableAutoJump,
  isAutoJumpDisabled = false,
}: EducationDrawerProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [highlightedSectionId, setHighlightedSectionId] = useState<string | null>(null);
  const [recommendedSectionId, setRecommendedSectionId] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [userOverrodeAutoJump, setUserOverrodeAutoJump] = useState(false);
  const [dontAutoJumpFuture, setDontAutoJumpFuture] = useState(false);
  
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentContainerRef = useRef<HTMLDivElement>(null);

  // Determine best matching section when opening
  useEffect(() => {
    if (open && topic && context) {
      let bestMatchId: string | null = null;
      let maxScore = -1;
      let bestPriority = -1;
      let bestMatchAllCount = -1;

      topic.sections.forEach((section, index) => {
        const matchAllRules = [...(section.matchAll || []), ...(section.match || [])];
        const matchAnyRules = section.matchAny || [];
        
        // 1. Check Eligibility
        
        // MatchAll: ALL must pass
        let matchAllCount = 0;
        let eligible = true;
        
        if (matchAllRules.length > 0) {
          for (const rule of matchAllRules) {
            if (evaluateRule(rule, context)) {
              matchAllCount++;
            } else {
              eligible = false;
              break;
            }
          }
        }
        if (!eligible) return; // Skip if matchAll failed

        // MatchAny: AT LEAST ONE must pass (if defined)
        let matchAnyCount = 0;
        if (matchAnyRules.length > 0) {
          let anyPassed = false;
          for (const rule of matchAnyRules) {
            if (evaluateRule(rule, context)) {
              anyPassed = true;
              matchAnyCount++;
            }
          }
          if (!anyPassed) {
            eligible = false;
          }
        }
        if (!eligible) return; // Skip if matchAny failed

        // If no rules defined at all, it's a generic section (eligible)
        // But we give it a lower score unless priority is high.

        // 2. Scoring
        const priority = section.priority || 0;
        
        // Formula: (priority * 100) + (matchAll * 10) + (matchAny * 5)
        const score = (priority * 100) + (matchAllCount * 10) + (matchAnyCount * 5);

        // 3. Winner Selection (Score > Priority > MatchAllCount > Index implicitly via order)
        if (score > maxScore) {
          maxScore = score;
          bestMatchId = section.id;
          bestPriority = priority;
          bestMatchAllCount = matchAllCount;
        } else if (score === maxScore) {
          // Tie-breakers
          if (priority > bestPriority) {
            bestMatchId = section.id;
            bestPriority = priority;
            bestMatchAllCount = matchAllCount;
          } else if (priority === bestPriority) {
            if (matchAllCount > bestMatchAllCount) {
              bestMatchId = section.id;
              bestMatchAllCount = matchAllCount;
            }
          }
        }
      });

      // Always track the recommended section
      setRecommendedSectionId(bestMatchId);

      // Only auto-jump/highlight if allowed and not overridden
      if (!userOverrodeAutoJump && !isAutoJumpDisabled) {
        setHighlightedSectionId(bestMatchId);

        // Scroll to highlighted section after animation frame
        if (bestMatchId) {
          // Only show banner if we jumped past the first section
          const firstSectionId = topic.sections[0]?.id;
          const didJump = bestMatchId !== firstSectionId;

          setTimeout(() => {
            sectionRefs.current[bestMatchId]?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
            
            if (didJump) {
              setShowBanner(true);
              // Auto-hide banner after 6 seconds
              setTimeout(() => setShowBanner(false), 6000);
            }
          }, 300); // Slight delay for sheet animation
        }
      } else {
        // If overridden or disabled, we don't highlight or scroll automatically
        setHighlightedSectionId(null);
      }
    } else if (!open) {
      // Reset when closed
      setHighlightedSectionId(null);
      setRecommendedSectionId(null);
      setShowBanner(false);
      setUserOverrodeAutoJump(false);
      setDontAutoJumpFuture(false);
    }
  }, [open, topic, context, isAutoJumpDisabled, userOverrodeAutoJump]);

  // If no topic is provided, we can't show anything
  if (!topic) return null;

  const handleClose = () => {
    if (dontShowAgain && onDismissTopic) {
      onDismissTopic(topicId);
    }
    
    if (dontAutoJumpFuture && onDisableAutoJump) {
      onDisableAutoJump(topicId);
    }
    
    onClose();
  };
  
  const handleShowEverything = () => {
    setUserOverrodeAutoJump(true);
    setHighlightedSectionId(null);
    setShowBanner(false);
    
    // Scroll content container to top
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleJumpToRecommended = () => {
    if (recommendedSectionId) {
      setHighlightedSectionId(recommendedSectionId);
      sectionRefs.current[recommendedSectionId]?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      // We don't flip userOverrodeAutoJump back to false to avoid the banner popping up again
      // We just manually perform the jump/highlight
    }
  };

  return (
    <Sheet open={open} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent 
        className="sm:max-w-md md:max-w-lg lg:max-w-xl overflow-hidden flex flex-col p-0"
      >
        {/* Banner */}
        {showBanner && (
          <div className="bg-indigo-600 text-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm animate-in slide-in-from-top-full duration-500 relative z-10 shadow-md">
            <div className="flex items-center gap-2">
              <Sparkles className="size-4 text-indigo-200 flex-shrink-0" />
              <span>Jumped to the most relevant guidance based on your answers.</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                variant="secondary" 
                size="sm" 
                className="h-7 text-xs bg-white/20 text-white hover:bg-white/30 border-none"
                onClick={handleShowEverything}
              >
                Show me everything
              </Button>
              <button 
                onClick={() => setShowBanner(false)}
                className="text-white/60 hover:text-white p-1"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}

        <div 
          ref={contentContainerRef}
          className="flex-1 overflow-y-auto px-6 py-6"
        >
          <SheetHeader className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <BookOpen className="size-3 mr-1" />
                Education
              </Badge>
            </div>
            <SheetTitle className="text-xl font-bold text-gray-900">
              {topic.title}
            </SheetTitle>
            <SheetDescription>
              Best practices and architectural guidance
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 pb-6">
            {/* Jump back link if overridden */}
            {userOverrodeAutoJump && recommendedSectionId && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="size-4 text-indigo-500" />
                  <span className="text-sm text-indigo-900 font-medium">Recommended section is available</span>
                </div>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={handleJumpToRecommended}
                  className="h-auto p-0 text-indigo-700 font-semibold"
                >
                  Jump to it
                </Button>
              </div>
            )}

            {topic.sections.map((section, index) => {
              const isHighlighted = section.id === highlightedSectionId;
              const isRecommended = section.id === recommendedSectionId;
              
              return (
                <div 
                  key={section.id || index} 
                  ref={el => sectionRefs.current[section.id] = el}
                  className={`space-y-3 transition-all duration-500 ${isHighlighted ? 'p-4 rounded-xl bg-blue-50/50 border-2 border-blue-200 shadow-sm -mx-4 sm:mx-0' : ''}`}
                >
                  <div className="flex items-center justify-between border-b pb-1">
                    <h3 className={`text-lg font-semibold ${isHighlighted ? 'text-blue-900' : 'text-gray-800'}`}>
                      {section.title}
                    </h3>
                    
                    {/* Badge Logic: 
                        1. If highlighted -> "Relevant for you" (Blue)
                        2. If NOT highlighted but IS recommended -> "Recommended" (Neutral/Indigo)
                    */}
                    {isHighlighted ? (
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200 gap-1 pl-1 pr-2">
                        <Sparkles className="size-3 fill-blue-300" />
                        Relevant for you
                      </Badge>
                    ) : isRecommended ? (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 gap-1 pl-1 pr-2">
                        <Sparkles className="size-3 fill-indigo-200" />
                        Recommended
                      </Badge>
                    ) : null}
                  </div>
                  
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {section.content}
                  </p>

                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-gray-600">
                      {section.bullets.map((bullet, i) => (
                        <li key={i}>{bullet}</li>
                      ))}
                    </ul>
                  )}

                  {/* Examples Grid */}
                  {(section.goodExample || section.riskyExample) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {section.goodExample && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-1 text-green-800 font-medium text-xs uppercase tracking-wide">
                            <CheckCircle className="size-3" />
                            Good Pattern
                          </div>
                          <p className="text-xs text-green-900">{section.goodExample}</p>
                        </div>
                      )}
                      {section.riskyExample && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-1 text-red-800 font-medium text-xs uppercase tracking-wide">
                            <XCircle className="size-3" />
                            Risky Pattern
                          </div>
                          <p className="text-xs text-red-900">{section.riskyExample}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Exception */}
                  {section.exception && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="size-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-yellow-800 mb-0.5">When to make an exception</p>
                          <p className="text-xs text-yellow-800">{section.exception}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <SheetFooter className="p-6 border-t border-gray-100 flex-col gap-4 bg-gray-50/50">
          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="dont-show-again" 
                  checked={dontShowAgain}
                  onCheckedChange={setDontShowAgain}
                />
                <Label htmlFor="dont-show-again" className="text-sm text-gray-600 cursor-pointer">
                  Don't show automatically next time
                </Label>
              </div>
              
              <Button onClick={handleClose} className="sm:w-auto">
                <CheckCircle2 className="size-4 mr-2" />
                Got it
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              <Switch 
                id="dont-auto-jump" 
                checked={dontAutoJumpFuture}
                onCheckedChange={setDontAutoJumpFuture}
              />
              <Label htmlFor="dont-auto-jump" className="text-sm text-gray-600 cursor-pointer">
                Don't auto-jump to sections in future
              </Label>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
